import React from 'react';
import { Tooltip } from '@mui/material';
import { Address } from '../../services/addressManagementService';

interface DataTableProps {
    data: Address[];
    isLoading: boolean;
    onSort: (field: string) => void;
    orderBy?: string;
    orderByDescending?: boolean;
    onViewPODs: (address: Address) => void;
    onViewDetails: (address: Address) => void;
    onPreviewMap: (address: Address) => void;
    mapPreviewLoadingTraceId?: string | null;
}

const DataTable: React.FC<DataTableProps> = ({
    data,
    isLoading,
    onSort,
    orderBy,
    orderByDescending,
    onViewPODs,
    onViewDetails,
    onPreviewMap,
    mapPreviewLoadingTraceId
}) => {

    const SortIcon = ({ field }: { field: string }) => {
        if (orderBy !== field) {
            return <i className="bi bi-arrow-down-up text-muted ms-1" style={{ fontSize: '0.8em', opacity: 0.5 }}></i>;
        }
        return (
            <i className={`bi bi-arrow-${orderByDescending ? 'down' : 'up'} ms-1 text-primary`}></i>
        );
    };

    const getConfidenceBadge = (confidence?: string | null) => {
        if (!confidence) return <span className="badge bg-secondary text-white-50">N/A</span>;
        switch (confidence) {
            case 'HIGH': return <span className="badge bg-success">HIGH</span>;
            case 'MEDIUM': return <span className="badge bg-warning text-dark">MEDIUM</span>;
            case 'LOW': return <span className="badge bg-danger">LOW</span>;
            default: return <span className="badge bg-secondary">{confidence}</span>;
        }
    };

    const getRadarGeoconfidenceBadge = (geoconfidence?: string | null) => {
        if (!geoconfidence) return <span className="badge bg-secondary text-white-50">N/A</span>;
        switch (geoconfidence) {
            case 'EXACT': return <span className="badge bg-success">EXACT</span>;
            case 'INTERPOLATED': return <span className="badge bg-warning text-dark">INTERPOLATED</span>;
            case 'FALLBACK': return <span className="badge bg-danger">FALLBACK</span>;
            default: return <span className="badge bg-secondary">{geoconfidence}</span>;
        }
    };

    const getCoordinateSourceBadge = (source?: string | null) => {
        if (!source) return <span className="badge bg-secondary text-white-50">N/A</span>;
        const colors: Record<string, string> = {
            GOOGLE: '#4285F4', // Google brand blue
            RADAR: '#000257', // Radar brand navy
            UBER: '#000000', // Uber brand black
            NONE: '#DC3545', // Red
            AdminPortal: '#6F42C1', // Purple
            AddressCorrectionPortal: '#FD7E14', // Orange
            AddressCorrectionAPI: '#20C997' // Teal
        };
        const backgroundColor = colors[source] || '#6c757d';
        return (
            <span className="badge" style={{ backgroundColor, color: '#fff' }}>
                {source}
            </span>
        );
    };

    const getRadarVerificationStatusBadge = (status?: string | null) => {
        if (!status) return <span className="badge bg-secondary text-white-50">N/A</span>;
        switch (status) {
            case 'VERIFIED': return <span className="badge bg-success">VERIFIED</span>;
            case 'PARTIALLY_VERIFIED': return <span className="badge bg-primary">PARTIALLY_VERIFIED</span>;
            case 'AMBIGUOUS': return <span className="badge bg-warning text-dark">AMBIGUOUS</span>;
            case 'UNVERIFIED': return <span className="badge bg-danger">UNVERIFIED</span>;
            default: return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleString();
        } catch (e) {
            return dateString;
        }
    };

    const GoogleNextActionTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>Google Possible Next Action:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>ACCEPT</strong> — The address is accurate and complete. It can be accepted as-is without further interaction from the user.</li>
                <li><strong>CONFIRM</strong> — The address is valid, but the API made minor corrections or inferred parts of it. The user should verify the "recommended" version.</li>
                <li><strong>CONFIRM_ADD_SUBPREMISES</strong> — The address is a multi-unit building, but the specific unit, suite, or apartment number is missing and should be provided.</li>
                <li><strong>FIX</strong> — Significant errors were found in the input (e.g., street or city). The user must edit the address before proceeding.</li>
            </ul>
        </div>
    );

    const RadarGeoconfidenceTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>Possible values:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>EXACT</strong> — Precise coordinates for a known address. No further action needed.</li>
                <li><strong>INTERPOLATED</strong> — Estimated coordinates between two known points. The user should verify the location.</li>
                <li><strong>FALLBACK</strong> — Coarse coordinates at postal code or city level. The user should provide a more specific address.</li>
            </ul>
        </div>
    );

    const RadarVerificationTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>Possible values:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>VERIFIED</strong> — The address is complete and uniquely matched. It can be accepted as-is.</li>
                <li><strong>PARTIALLY_VERIFIED</strong> — The address partially matched. The user should verify the recommended version.</li>
                <li><strong>AMBIGUOUS</strong> — The address closely matches multiple results. The user should clarify the address before proceeding.</li>
                <li><strong>UNVERIFIED</strong> — The address could not be verified. The user must edit the address before proceeding.</li>
            </ul>
        </div>
    );

    const DestinationTypeTooltip = (
        <div style={{ fontSize: '0.85rem' }}>
            <strong>USPS Destination Types:</strong>
            <ul className="mb-0 ps-3">
                <li><strong>F — Firm</strong> — The address belongs to a specific company or organization at that location (e.g., a named business building)</li>
                <li><strong>G — General Delivery</strong> — A temporary mail service for people without a permanent address, typically at a post office</li>
                <li><strong>H — High-rise</strong> — A multi-unit building such as an apartment complex, condo, or office tower where a unit number is expected</li>
                <li><strong>P — PO Box</strong> — A locked box at a post office, not a physical delivery location</li>
                <li><strong>R — Rural Route</strong> — A mail route in a rural area, typically without a traditional street address</li>
                <li><strong>S — Street</strong> — A standard street address with a house or building number on a named street</li>
            </ul>
        </div>
    );

    const getDestinationTypeLabel = (type: string): string => {
        const labels: Record<string, string> = {
            'F': 'F — Firm',
            'G': 'G — General Delivery',
            'H': 'H — High-rise',
            'P': 'P — PO Box',
            'R': 'R — Rural Route',
            'S': 'S — Street'
        };
        return labels[type] || type;
    };

    const getDestinationTypeDescription = (type: string): string => {
        const descriptions: Record<string, string> = {
            'F': 'Firm — The address belongs to a specific company or organization at that location (e.g., a named business building)',
            'G': 'General Delivery — A temporary mail service for people without a permanent address, typically at a post office',
            'H': 'High-rise — A multi-unit building such as an apartment complex, condo, or office tower where a unit number is expected',
            'P': 'PO Box — A locked box at a post office, not a physical delivery location',
            'R': 'Rural Route — A mail route in a rural area, typically without a traditional street address',
            'S': 'Street — A standard street address with a house or building number on a named street'
        };
        return descriptions[type] || 'Unknown destination type';
    };

    return (
        <div className="table-responsive shadow-sm border rounded">
            <table className="table table-hover table-striped mb-0 align-middle">
                <thead className="table-light">
                    <tr>
                        <th scope="col" onClick={() => onSort('id')} className="cursor-pointer text-nowrap user-select-none">
                            ID / Trace ID <SortIcon field="id" />
                        </th>
                        <th scope="col" onClick={() => onSort('street1')} className="cursor-pointer text-nowrap user-select-none">
                            Full Address <SortIcon field="street1" />
                        </th>
                        <th scope="col" className="text-center">Internal Geofence</th>
                        <th scope="col" className="text-nowrap">
                            <div className="d-flex align-items-center gap-1">
                                Google Status
                                <Tooltip title={GoogleNextActionTooltip} arrow placement="top">
                                    <i className="bi bi-info-circle ms-1" style={{ cursor: 'help', fontSize: '0.9em' }}></i>
                                </Tooltip>
                            </div>
                        </th>
                        <th scope="col" className="text-center">
                            <div className="d-flex align-items-center gap-1 justify-content-center">
                                Radar Status
                                <Tooltip title={RadarVerificationTooltip} arrow placement="top">
                                    <i className="bi bi-info-circle" style={{ cursor: 'help', fontSize: '0.9em' }}></i>
                                </Tooltip>
                            </div>
                        </th>
                        <th scope="col" className="text-center">
                            <div className="d-flex align-items-center gap-1 justify-content-center">
                                Radar Confidence
                                <Tooltip title={RadarGeoconfidenceTooltip} arrow placement="top">
                                    <i className="bi bi-info-circle" style={{ cursor: 'help', fontSize: '0.9em' }}></i>
                                </Tooltip>
                            </div>
                        </th>

                        <th scope="col" className="text-center">
                            <div className="d-flex align-items-center gap-1 justify-content-center">
                                Address Classification
                                <Tooltip title={DestinationTypeTooltip} arrow placement="top">
                                    <i className="bi bi-info-circle" style={{ cursor: 'help', fontSize: '0.9em' }}></i>
                                </Tooltip>
                            </div>
                        </th>

                        <th scope="col" className="text-center">
                            <div className="d-flex align-items-center gap-1 justify-content-center">
                                PODs
                                <Tooltip
                                    title={
                                        <div style={{ fontSize: '0.85rem' }}>
                                            <strong>Proof of Delivery Images:</strong>
                                            <p className="mb-0">Number of POD images attached to this address</p>
                                        </div>
                                    }
                                    arrow
                                    placement="top"
                                >
                                    <i className="bi bi-info-circle" style={{ cursor: 'help', fontSize: '0.9em' }}></i>
                                </Tooltip>
                            </div>
                        </th>

                        <th scope="col" onClick={() => onSort('createdAt')} className="cursor-pointer text-nowrap user-select-none">
                            Created At <SortIcon field="createdAt" />
                        </th>
                        <th scope="col" className="text-center">Source</th>
                        <th scope="col" className="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={11} className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 text-muted">Loading addresses...</p>
                            </td>
                        </tr>
                    ) : data.length === 0 ? (
                        <tr>
                            <td colSpan={11} className="text-center py-5">
                                <i className="bi bi-inbox fs-1 text-muted opacity-50"></i>
                                <p className="mt-2 text-muted">No addresses found matching your filters.</p>
                            </td>
                        </tr>
                    ) : (
                        data.map((address) => (
                            <tr key={address.id}>
                                <td>
                                    <div className="d-flex align-items-center gap-1">
                                        <small className="font-monospace text-muted">#{address.id}</small>
                                        {address.hasGeminiPreprocessing ? (
                                            <i className="bi bi-robot text-primary" style={{ fontSize: '0.8em' }} title="Gemini AI preprocessed"></i>
                                        ) : (
                                            <i className="bi bi-robot text-muted opacity-25" style={{ fontSize: '0.8em' }} title="Not preprocessed by Gemini"></i>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <small className="font-monospace">
                                            <a href={`/address-management/${address.traceId}`} className="text-decoration-none" onClick={(e) => e.stopPropagation()}>
                                                {address.traceId}
                                            </a>
                                        </small>
                                        <button
                                            className="btn btn-link btn-sm p-0 border-0 text-muted"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(address.traceId || '');
                                            }}
                                            title="Copy trace ID to clipboard"
                                        >
                                            <i className="bi bi-clipboard" style={{ fontSize: '0.8em' }}></i>
                                        </button>
                                    </div>
                                </td>
                                <td style={{ maxWidth: '200px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                    <div className="fw-medium text-dark">
                                        {address.street1}
                                        {address.street2 && (
                                            <>
                                                <br />
                                                {address.street2}
                                            </>

                                        )}
                                        <br />
                                        {address.city}, {address.stateCode} {address.zip}{address.zipSuffix ? `-${address.zipSuffix}` : ''}
                                        {address.supersededByAddressId && (
                                            <Tooltip
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            maxWidth: 480,
                                                            bgcolor: '#212529',
                                                            p: 1.5,
                                                            boxShadow: 3
                                                        }
                                                    },
                                                    arrow: {
                                                        sx: { color: '#212529' }
                                                    }
                                                }}
                                                title={
                                                    <div style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
                                                        <strong style={{ fontSize: '0.95rem' }}>Address corrected</strong>
                                                        <p className="mb-2">This record was superseded by a newer record{address.supersededByTraceId ? ' — click to open it' : ''}.</p>
                                                        {address.supersededByFullAddress && (
                                                            <p className="mb-2">
                                                                <span className="text-white-50">New address:</span>{' '}
                                                                {address.supersededByFullAddress}
                                                            </p>
                                                        )}
                                                        <p className="mb-0">
                                                            <span className="text-white-50">ID:</span>{' '}
                                                            <span className="font-monospace">#{address.supersededByAddressId}</span>
                                                            {address.supersededByTraceId && (
                                                                <>
                                                                    <br />
                                                                    <span className="text-white-50">Trace ID:</span>{' '}
                                                                    <span className="font-monospace" style={{ whiteSpace: 'nowrap' }}>{address.supersededByTraceId}</span>
                                                                </>
                                                            )}
                                                        </p>
                                                    </div>
                                                }
                                                arrow
                                                placement="top"
                                            >
                                                {address.supersededByTraceId ? (
                                                    <a
                                                        href={`/address-management/${address.supersededByTraceId}`}
                                                        className="text-decoration-none ms-2"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <i className="bi bi-arrow-repeat text-success"></i>
                                                    </a>
                                                ) : (
                                                    <i className="bi bi-arrow-repeat text-success ms-2"></i>
                                                )}
                                            </Tooltip>
                                        )}
                                    </div>
                                </td>

                                {/* Geofence Confidence */}
                                <td className="text-center">
                                    {getConfidenceBadge(address.geoFenceConfidence)}
                                </td>
                                <td>
                                    {address.googlePossibleNextAction ? (
                                        (() => {
                                            let badgeClass = 'bg-secondary';
                                            switch (address.googlePossibleNextAction) {
                                                case 'ACCEPT':
                                                    badgeClass = 'bg-success';
                                                    break;
                                                case 'CONFIRM':
                                                    badgeClass = 'bg-primary';
                                                    break;
                                                case 'CONFIRM_ADD_SUBPREMISES':
                                                    badgeClass = 'bg-warning text-dark';
                                                    break;
                                                case 'FIX':
                                                    badgeClass = 'bg-danger';
                                                    break;
                                            }
                                            return (
                                                <span className={`badge ${badgeClass}`} style={{ fontSize: '0.75em' }}>
                                                    {address.googlePossibleNextAction}
                                                </span>
                                            );
                                        })()
                                    ) : (
                                        <span className="text-muted small">-</span>
                                    )}
                                </td>

                                {/* Radar Verification Status */}
                                <td className="text-center">
                                    {getRadarVerificationStatusBadge(address.radarVerificationStatus)}
                                </td>


                                {/* Radar Geoconfidence */}
                                <td className="text-center">
                                    {getRadarGeoconfidenceBadge(address.radarGeoconfidence)}
                                </td>

                                {/* Address Classification */}
                                <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                        <div className="d-flex align-items-center gap-1">
                                            <small className="text-muted">Apt:</small>
                                            {address.isApartment ? (
                                                <span className="badge bg-success">Yes</span>
                                            ) : (
                                                <span className="badge bg-secondary">No</span>
                                            )}
                                        </div>
                                        {address.destinationType ? (
                                            <Tooltip
                                                title={getDestinationTypeDescription(address.destinationType)}
                                                arrow
                                                placement="top"
                                            >
                                                <span style={{ cursor: 'help', borderBottom: '1px dotted #007bff', fontSize: '0.9em' }}>
                                                    {getDestinationTypeLabel(address.destinationType)}
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <span className="text-muted small">-</span>
                                        )}
                                    </div>
                                </td>

                                {/* POD Count */}
                                <td
                                    className="text-center"
                                    style={{ cursor: address.addressPods && address.addressPods.length > 0 ? 'pointer' : 'default' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (address.addressPods && address.addressPods.length > 0) {
                                            onViewPODs(address);
                                        }
                                    }}
                                    title={address.addressPods && address.addressPods.length > 0 ? 'Click to view POD images' : 'No POD images available'}
                                >
                                    {address.addressPods && address.addressPods.length > 0 ? (
                                        <span className="badge bg-primary">
                                            <i className="bi bi-images me-1"></i>
                                            {address.addressPods.length}
                                        </span>
                                    ) : (
                                        <span className="badge bg-secondary text-white-50">
                                            <i className="bi bi-images me-1"></i>
                                            0
                                        </span>
                                    )}
                                </td>

                                <td className="small text-muted">
                                    {formatDate(address.createdAt)}
                                </td>

                                {/* Coordinate Source */}
                                <td className="text-center">
                                    {getCoordinateSourceBadge(address.coordinateSource)}
                                </td>

                                {/* Actions */}
                                <td className="text-center">
                                    <button
                                        className="btn btn-outline-primary btn-sm me-2"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewDetails(address);
                                        }}
                                        title="View details"
                                    >
                                        <i className="bi bi-eye"></i>
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={mapPreviewLoadingTraceId === address.traceId}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onPreviewMap(address);
                                        }}
                                        title="Preview on the map"
                                    >
                                        {mapPreviewLoadingTraceId === address.traceId ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                            <i className="bi bi-map"></i>
                                        )}
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default DataTable;
